import {memo} from 'react';
import {parseEventDateTime} from '~/common/utils/time';
import {usePostContext} from '~/ViewInvitation/components/Guestlist/hooks/usePostContext';
import {Likes} from '~/ViewInvitation/components/Guestlist/Post/Likes';
import type {Post as PostType} from '~/ViewInvitation/types/api';
import type {Guest} from '~/ViewInvitation/types/guests';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';

import * as styles from './Comment.module.scss';

interface CommentProps {
  guest: Guest;
  post: PostType;
  onReply: () => void;
  onCancel: () => void;
  isReplying: boolean;
}

export const Comment = memo(({guest, post, isReplying, onCancel, onReply}: CommentProps) => {
  const {created, content, likes, likedBy} = post || {};
  const {isEditing: isPostEditing} = usePostContext();
  const datetime = parseEventDateTime(created || '', 'utc');
  const timestamp = datetime.isValid ? datetime.setZone('local').toRelative() : '';

  return (
    <div className={styles.comment}>
      <div className={styles.commentContent}>
        <Typography variant="list1" size="large">
          {guest.name} RSVP'd <strong>{content?.response}</strong>
        </Typography>
        {guest.comment && <Typography variant="paragraph3">{guest.comment}</Typography>}
        <div className={styles.commentActions}>
          <Typography variant="paragraph4" color="pebble">
            {timestamp}
          </Typography>
          <Button
            className={styles.replyButton}
            data-qa-id="reply-button"
            variant="link"
            size="small"
            onClick={isReplying ? onCancel : onReply}
            disabled={!isReplying && isPostEditing}
            title={
              !isReplying && isPostEditing
                ? 'Please finish replying to or editing the other comment'
                : ''
            }
          >
            {isReplying ? 'Cancel' : 'Reply'}
          </Button>
        </div>
      </div>
      <Likes likes={likes} likedBy={likedBy} postId={post.id} />
    </div>
  );
});

Comment.displayName = 'Comment';
